import { createPortal, flushSync } from 'react-dom';
import { forwardRef, useEffect, useRef, useState, useImperativeHandle } from 'react';
import Cookies from 'js-cookie';
import LockedInfoBanner from './Overblick/components/LockedInfoBanner';
import { TailwindWrapper } from '@utils/TailwindWrapper';

import styled from "styled-components";
import pointInPolygon from 'point-in-polygon';
import Joyride from 'react-joyride';

import {
    formatNumber,
    readKartaMapOptionsFromStorage,
    persistKartaMapOptionsToStorage,
} from './utils';
import { TwModal } from '@ui-component-library';
import { SubscriptionCoverRow } from './Overblick/components/SubscriptionCovers';
import PropSelector from './comps/PropSelector';
import VBLeaflet from '@vb-js-commons/new/vbLeaflet';
import { swapCoords } from '@vb-js-commons/new/leafletConfig';

import { usePlusBlockerDialog } from "@components/PlusBlockerDialog/PlusBlockerDialog.context";
import { useVisaValdaStore } from './store/VisaValdaStore';
import { useNavigationStore } from './store/NavigationStore';
import { usePropsDataStore } from './store/PropsDataStore';

import { FellingClassesLegend, ToolboxContainer, MapTools, setUserLocation, coptureLayerList } from './helpers';
import { ErrorModal } from '../../modals';

import { Button, Icon, Text, Dialog, Switch, Drawer, SliderBar } from "@ui-component-library";

import VBInput from '@vb-js-commons/new/vbInput';
import VBTextarea from '@vb-js-commons/new/vbTextarea';
import VBDropdown from '@vb-js-commons/new/vbDropdown';
import VBLoadingScreen from '@vb-js-commons/vbLoadingScreen';
import VBToggle from '@vb-js-commons/new/vbToggle';
import { useDemoMinskogStore } from './store/DemoMinskogStore';
import { DEMO_PROMPTS } from './constants/demoPrompts';

/**
 * Section polygons are inside L.FeatureGroups on the map, so a flat map.eachLayer
 * will not see them. Walk groups and return the drawn polygon for the given avdelning nr.
 */
function findDrawnSectionPolygonByNr(map, sectionNr) {
    const matchNr = (layer) => layer.data?.nr != null && String(layer.data.nr) === String(sectionNr);
    let result = null;
    map.eachLayer((layer) => {
        if (typeof layer.eachLayer === 'function') {
            layer.eachLayer((sub) => {
                if (sub?.options?.drawn && sub.options.type === 'polygon' && matchNr(sub)) {
                    result = sub;
                }
            });
        } else if (layer?.options?.drawn && layer.options.type === 'polygon' && matchNr(layer)) {
            result = layer;
        }
    });
    return result;
}

function applySectionBaseStyle(layer) {
    if (!layer?.baseStyle) return;
    layer.setStyle({ ...layer.baseStyle, dashArray: null });
}

function applySectionHoverStyle(layer, sectionOpacity) {
    layer.setStyle({
        color: 'white',
        weight: 3.3,
        fillOpacity: Math.min(sectionOpacity + 0.2, 1),
        dashArray: '10, 10',
    });
}

function applySectionSelectedStyle(layer) {
    const { baseStyle } = layer;
    layer.setStyle({
        fillColor: baseStyle.fillColor,
        fillOpacity: 0.8,
        weight: baseStyle.weight + 1.5,
        color: 'white',
        dashArray: null,
    });
}

const CustomScrollbar = styled.div`
    height: 90%;
    overflow-y: auto;
    overflow-x: hidden;

    ::-webkit-scrollbar {
        width: 10px;
    }

    ::-webkit-scrollbar-thumb {
        background: #023436;
        border-radius: 10px;
    }
`

const EditUIContainer = styled.div`
    width: 250px;
    right: 60px;
    top: 90px;

    @media (min-width: 992px) {
        top: 10px;
    }
`

const CoptureMapContainer = styled.div`
    left: 10px;
    top: 90px;
    @media (min-width: 992px) {
        top: 10px;
    }
`
const PremiumCover = styled.a`
    transition: background-color ease 300ms;
    z-index: 1;
    max-width: 70%;
    &:hover {
        background-color: #023436 !important;
        color: #FFFFFF !important;
    }
`

const infoSteps = [
    {
        target: '#propSelector',
        content: 'Klicka här för att välja eller söka efter en fastighet',
        disableBeacon: true,
    },
    {
        target: '#leafletLayerButton',
        content: 'Klicka här för att ändra karttyp samt lägga till kartdata och se observationer.',
        disableBeacon: true,
    },
    {
        target: '#toolkitButton',
        content: 'Här kan du redigera avdelningar, markörer samt visa din plats mm.',
        disableBeacon: true,
    },
    {
        target: "#drawButton",
        content: "Här kan du rita nya avdelningar",
        disableBeacon: true,
    },
    {
        target: "#editSectionButton",
        content: "Här kan du redigera befintliga avdelningar",
        disableBeacon: true,
    },
    {
        target: "#deleteSectionButton",
        content: "Här kan du radera avdelningar",
        disableBeacon: true,
    },
    {
        target: "#getLocation",
        content: "Här kan du visa din plats i kartan",
        disableBeacon: true,
    },
    {
        target: "path.leaflet-interactive",
        content: "Klicka på en avdelning i din skogsbruksplan för att se information",
        disableBeacon: true,
    },
    {
        target: screen.width >= 992 ? "#menuoverblick" : "#quickmenuoverblick",
        content: "Här kan du få en överblick av din skogsdata",
        disableBeacon: true,
    },
    {
        target: "#helpButton",
        content: "Klicka här för att visa guiden igen eller om du behöver mer hjälp ring oss på 08-33 99 44",
        disableBeacon: true,
    }
]

const freemiumFeatures = ["Satellit", "Avd. nr"];

//export default function Karta() {
const Karta = forwardRef((props, ref) => {
    const { mapImmersive = false, onToggleMapImmersive } = props;
    const { visaValda, setVisaValda } = useVisaValdaStore();
    const { view, setView } = useNavigationStore()
    const { props: properties, setOneProp, setProps } = usePropsDataStore()
    const { openPlusBlockerDialog } = usePlusBlockerDialog();

    const {isDemo, DemoBlockFunctionWrapper} = useDemoMinskogStore();

    // componentDidMount trackers
    const mapOptionsMount = useRef(true);

    const leaflet = useRef(null);
    const hoveredSectionLayerRef = useRef(null);
    const selectedSectionLayerRef = useRef(null);
    const mapMouseLeaveHandlerRef = useRef(null);

    useEffect(() => {
        const map = leaflet.current?.state?.map;
        if (map) {
            requestAnimationFrame(() => map.invalidateSize());
        }
    }, [mapImmersive]);

    useEffect(() => {
        const onFullscreenChange = () => {
            const map = leaflet.current?.state?.map;
            if (map) {
                requestAnimationFrame(() => map.invalidateSize());
            }
        };
        document.addEventListener('fullscreenchange', onFullscreenChange);
        document.addEventListener('webkitfullscreenchange', onFullscreenChange);
        return () => {
            document.removeEventListener('fullscreenchange', onFullscreenChange);
            document.removeEventListener('webkitfullscreenchange', onFullscreenChange);
        };
    }, []);

    const [refresh, doRefresh] = useState(false);
    const [loading, setLoading] = useState(true);
    const [viewportHeight, setViewportHeight] = useState(window.innerHeight);

    const [mapTools, setMapTools] = useState(false);
    const [mapOptions, setMapOptions] = useState(() => readKartaMapOptionsFromStorage(view));

    const [showSectNr, setShowSectNr] = useState(false);
    const [legend, setLegend] = useState([]);
    const [showLegend, setShowLegend] = useState(false);

    const [joyride, setShowJoyride] = useState(false);
    const [helpSteps, setHelpSteps] = useState(0);
    const [newCoptureLayer, setNewCoptureLayer] = useState(null);
    const [copturePointLayers, setCopturePointLayers] = useState([]);
    const [propDfr,setPropDfr] = useState(false);

    // Modals
    const [infoModal, setInfoModal] = useState(false);
    const [markerModal, setMarkerModal] = useState({show: false});
    const [selectPropModal, setSelectPropModal] = useState(false);
    const [errorModal, setErrorModal] = useState(false);
    const [deleteMarkerModal, setDeleteMarkerModal] = useState(false);
    const [mobileCoptureModalOpen, setMobileCoptureModalOpen] = useState(false);

    const activeProps = properties.filter(p => p.active == true);
    const activeCanEdit =
        activeProps.length > 0 &&
        activeProps.every((p) => (p.role ? p.role !== 'view' : !p.is_shared));

    useEffect(() => {
        const activeCoptureLayerCount = coptureLayerList(properties.filter(p => p.active)).length;
        if (activeCoptureLayerCount <= 1) setMobileCoptureModalOpen(false);
    }, [properties]);

    useImperativeHandle(ref, () => ({
        handleHistory: section_nr => {
            if (section_nr && !loading) {
                const map = leaflet.current?.state?.map;
                const layer = findDrawnSectionPolygonByNr(map, section_nr);
                if (layer) {
                    layer.fire('click');
                }
            }
        }
    }))

    const generateLegend = () => {
        let legend = new Set()
        properties.filter(p => p.active == true).map(p => {
            if('data' in p) {
                p.data.legends.felling_classes.map(c => legend.add(c))
            }
        })
        return Array.from(legend)
    }

    const handleUserSubscription = (onPlus, onLocked, extraCondition = null) => {
        const hasSubscription = propDfr || extraCondition;

        return hasSubscription ? onPlus() : onLocked();
    };

    const setCoptureLayer = (obj) => {
        obj.options = {maxZoom: 22}
        
        /*
        This was used in the old style point layer. Leaving it for now but can probably be deleted if we see no usecase afterwards. Probably the whole function can be removed. We will see.

        obj.extraData = (data) => {
            let output = {}
            let elem = data.documentElement.getElementsByTagName("VBPostGis:SYMBOL")[0]
            if(elem) {
                output["Analys"] = elem.innerHTML.toString().replaceAll("Ã¤","ä")
            }
            return output
        }*/
        return obj
    }

    const toggleCoptureLayer = (item) => {
        if(item.obj.style == 'raster') {
            leaflet.current.toggleMapLayer(item.obj)
        }
        
        if(item.obj.style == 'point') {
            const map = leaflet.current.state.map
            if(map.hasLayer(item.featureGroup)) {
                item.active = false
                map.removeLayer(item.featureGroup)
            } else {
                item.active = true
                item.featureGroup.addTo(map)
            }
        }
        
        doRefresh(true)
    }

    const enableCoptureLayerActiveProp = (applySectionOpacityHeuristic = false) => {
        const activePropsRefs = properties.filter(p => p.active == true).map(prop => prop.ref)
        const coptureLayers = coptureLayerList(properties)
        const coptureLayersActive = coptureLayerList(properties.filter(p => p.active == true))

        let newMapOptions = {}
        if (applySectionOpacityHeuristic) {
            if(coptureLayersActive.length > 0) {
                newMapOptions.sectionOpacity = .3
            } else {
                newMapOptions.sectionOpacity = .7
            }
        }

        let activeMapLayers = []
        leaflet.current.state.mapLayers.eachLayer(l => activeMapLayers.push(l.name))

        let pointLayers = []

        // We go over each existing copture layer and check if it should be activated or not. Simply comparing the active ones and should be active ones together and then toggling them if these 2 values are not the same.
        coptureLayers.forEach(async l => {
            if(activePropsRefs.includes(l.ref) != activeMapLayers.includes(l.obj.layer)) {
                if(l.obj.style == 'raster') {
                    leaflet.current.toggleMapLayer(setCoptureLayer(l.obj));
                }
                if(l.obj.style == 'point') {
                    pointLayers.push(l)
                }
            }
        })

        setCopturePointLayers(pointLayers)

        doRefresh(true);
        return {mapOptions: newMapOptions, pointLayers: pointLayers}
    }

    const waitForMapThenSetData = async () => {
        const currentProperties = usePropsDataStore.getState().props;
        const activeProps = currentProperties.filter(p => p.active);
        const propsToFetch = activeProps.filter(p => p.status === 'data' && p.data && p.ready)

        if(propsToFetch.length != activeProps.length) {
            setTimeout(() => waitForMapThenSetData(), 100)
        } else {
            if(!leaflet.current?.state?.map) {
                setTimeout(() => waitForMapThenSetData(), 100)
            } else {
                const hasExtraLayers = propsToFetch.some(prop => prop.data.extra_layers && prop.data.extra_layers.length > 0)
                const enableResp = hasExtraLayers
                    ? enableCoptureLayerActiveProp(false)
                    : { mapOptions: {}, pointLayers: [] }

                setMapOptions((prev) => ({ ...prev, ...enableResp.mapOptions }))
                setMapData(true, enableResp.mapOptions, enableResp.pointLayers)
                setLegend(generateLegend())
                setLoading(false)
            }
        }
    }

    useEffect(() => {
        // This is for safari users to fix a weird scrolling behaviour from Safari itself
        document.body.classList.add('fixed-top');
        // This is for the URL bar in mobile browsers for when the appears/dissapears and the viewport of the window changes.
        window.addEventListener('resize', () => setViewportHeight(window.innerHeight));

        waitForMapThenSetData()

        return () => {
            document.body.classList.remove('fixed-top');
            window.removeEventListener('resize', () => setViewportHeight(window.innerHeight));
        }
    }, []);

    useEffect(() => {
        if(visaValda) {
            const enableResp = enableCoptureLayerActiveProp(true)
            setLegend(generateLegend())
            setMapOptions((prev) => ({ ...prev, ...enableResp.mapOptions }))
            setMapData(true, enableResp.mapOptions, enableResp.pointLayers)
            setLoading(false)
            
            setVisaValda(false)
        }
    }, [visaValda]);

    useEffect(() => {
        if(refresh) {
            doRefresh(false)
        }    
    }, [refresh]);

    useEffect(() => {
        persistKartaMapOptionsToStorage(mapOptions);
    }, [mapOptions]);

    useEffect(() => {
        if(!mapOptionsMount.current) {
            setMapData(false)
        }
    }, [mapOptions])

    useEffect(() => {
        if(leaflet.current.state.map) {
            setMapData(false)
        }
    }, [showSectNr]);

    useEffect(() => {
        if(newCoptureLayer != false) {
            const now = new Date()
            const twoWeeksPast = new Date(now.getTime() - (14 * 24 * 60 * 60 * 1000))
            let earliestDate = null
            coptureLayerList(properties).forEach(l => {
                const date = new Date(l.obj.created_at)
        
                if(!earliestDate || date > earliestDate.date) {
                    earliestDate = {date: date, obj: l}
                }
            })

            if(earliestDate && earliestDate.date > twoWeeksPast) {
                setNewCoptureLayer(earliestDate.obj)
            }
        }
    }, [properties])

    const fellingClassesOrder = ['S1', 'S2', 'G1', 'G2', 'R', 'K', 'IA', 'R1']

    const mapMarkers = [
        `${Window.static_path}img/map-markers/marker-location.svg`,
        `${Window.static_path}img/map-markers/marker-mushroom.svg`,
        `${Window.static_path}img/map-markers/marker-tree.svg`,
        `${Window.static_path}img/map-markers/marker-stop.svg`,
        `${Window.static_path}img/map-markers/marker-windy.svg`,
        `${Window.static_path}img/map-markers/marker-treebug.svg`,
        `${Window.static_path}img/map-markers/marker-onfire.svg`
    ]

    const markerPopupHtml = (marker, propID, propType, editable) => {
        const html = document.createElement("div")
        html.innerHTML = `
            <b>${marker.name}</b><br />
            ${marker.comment}<br />
        `

        if(editable) {
            const editButton = document.createElement('span')
            editButton.role = 'button'
            editButton.classList.add('underline', 'mr-2');
            editButton.innerHTML = 'Ändra'
            editButton.onclick = function() {
                setMarkerModal({show: true, selected: marker.icon, name: marker.name, comment: marker.comment, id: marker.id, ref: propID, type: propType })
            }
            const deleteButton = document.createElement('span')
            deleteButton.classList.add('highlight', 'underline');
            deleteButton.role = 'button'
            deleteButton.innerHTML = 'Radera'
            deleteButton.onclick = function() {
                setDeleteMarkerModal({id: marker.id, ref: propID, type: propType})
            }

            html.appendChild(editButton)
            html.appendChild(deleteButton)
        }

        return html
    }

    const openSectionDetails = (sectionNr) => {
        const newView = { ...view, section: sectionNr }
        setView(newView);
        history.pushState(newView, '', '');
    }

    const setMapData = (zoomToProps=true, newMapOptions={}, pointLayers=copturePointLayers, selectedLayerNr=null) => {
        // Guard clause to check if the map is available to avoid errors when coming from the NoActivePropsView.
        const map = leaflet.current?.state?.map;
        if (!map) return;

        const newOptions = {...mapOptions, ...newMapOptions}

        hoveredSectionLayerRef.current = null;
        selectedSectionLayerRef.current = null;
        const propsToUse = usePropsDataStore.getState().props;
        const activeProps = propsToUse.filter(p => p.active)
        const allDfr = activeProps.every(prop => prop.dfr);
        setPropDfr(allDfr);
        let sections = L.featureGroup([])
        let warningSections = L.featureGroup([])
        let markers = L.featureGroup([])
        let borders = L.featureGroup([])
        let labels = L.featureGroup([])

        leaflet.current.clearMap()
        // This code could be better by setting {drawn: true} to each marker since the clearMap() would remove it automatically then. This is a temporary solution.
        copturePointLayers.filter(l => l.active).forEach(l => map.removeLayer(l.featureGroup))

        const baseIconSettings = {
            iconSize: [40, 57], // size of the icon
            shadowSize: [40, 57], // size of the shadow
            iconAnchor: [20, 57], // point of the icon which will correspond to marker's location (actual length divide by 2)
            shadowAnchor: [40, 57],  // the same for the shadow
            popupAnchor: [0, -32] // point from which the popup should open relative to the iconAnchor
        }

        if(pointLayers) {
            pointLayers.forEach(layer => {
                if(!layer.featureGroup) {
                    let group = L.featureGroup([])
                    let pointJson = L.geoJSON(layer.obj.data, {
                        pointToLayer: function (feature, latlng) {
                            const content = feature.properties.SYMBOL.split(':');

                            let name = content[0];
                            let comment = content[1]
                            let svgImg = 'marker-location.svg'
                            if(content[1] == 'Insektskada') {
                                svgImg = 'marker-treebug.svg'
                            }
                            if(content[1] == 'Vindfällen') {
                                svgImg = 'marker-windy.svg'
                            }
                            if(content[1] == 'Djur') {
                                svgImg = 'marker-deer.svg'
                                name = feature.properties.Djur;

                                if (name == "Vildsvin"){
                                    svgImg = 'marker-boar.svg'
                                } else if(name == "R�djur"){
                                    svgImg = 'marker-roe-deer.svg'
                                } else if(name == "�lg"){
                                    svgImg = 'marker-moose.svg'
                                }

                                comment = "Antal: " + feature.properties.Antal?.toString();
                            }

                            const marker = L.marker(latlng, {
                                icon: L.icon({ ...baseIconSettings, iconUrl: `${Window.static_path}img/map-markers/${svgImg}` })
                            });

                            marker.bindPopup(markerPopupHtml({name: name, comment: comment}, layer.ref, 'PROP', false))
                            return marker
                        }
                    })
                    group.addLayer(pointJson)
                    layer.featureGroup = group
                    layer.active = true
                    group.addTo(map)
                } else {
                    if(layer.active) {
                        layer.featureGroup.addTo(map)
                    }
                }
            })
        }
        
        activeProps.map(prop => {
            // Creating property borders
            if('border' in prop) {
                prop.border.map(b => {
                    let layer = L.polygon(swapCoords(b), {color: "#FFFFFF", fillOpacity: 0, interactive: false, type: 'polygon'})
                    leaflet.current.setLayer(layer)
                    layer.addTo(borders)
                })
            }

            // Creating all the markers for the property
            if(newOptions.showMarkers) {
                const propCanEdit = prop.role ? prop.role !== 'view' : !prop.is_shared
                prop.data.markers.map(m => {
                    var icon = L.icon({ ...baseIconSettings, iconUrl: m.icon })
    
                    let mark = L.marker([m.y, m.x], { icon: icon, type: 'marker' })
                    mark.data = { djangoID: m.id, ref: prop.ref, type: prop.type }
    
                    mark.bindPopup(markerPopupHtml(m, prop.ref, prop.type, propCanEdit))
    
                    mark.addTo(markers)
                })
    
                leaflet.current.setLayers(markers)
            }

            // Creating section polygons for property
            prop.data.sections.map(sect => {
                let color = FellingClassesLegend[sect.felling_class] ? FellingClassesLegend[sect.felling_class].color : "#bb0000"

                let fillP = null;
                if(sect.is_fucked){
                    // 1. Create the base pattern object
                    fillP = new L.Pattern({
                        angle:45
                    });
                    var rect1 = new L.PatternRect({ 
                        x: 0, y: 0, width: 10, height: 10, color: 'red', fillOpacity: 0.5 
                    });
                    var rect2 = new L.PatternRect({ 
                        x: 10, y: 10, width: 10, height: 10, color: 'red', fillOpacity: 0.5 
                    });
                    fillP.addShape(rect1);
                    fillP.addShape(rect2);
                    fillP.addTo(map);
                    color = "#ff0000"
                }  
                else if(sect.felling_class == "NS"){
                    
                    fillP = new L.StripePattern({
                        weight: 2,         // Width of the stripe
                        spaceWeight: 5,    // Width of the gap between stripes
                        color: '#ffff00',  // Color of the stripe
                        opacity: 1,        // Opacity of the stripe
                        angle: 45          // Rotation of the pattern in degrees
                    });

                    // 2. IMPORTANT: Add the pattern to the map
                    fillP.addTo(map);
                    color = "#ffff00"
                }

                const baseStyle = {
                    color: color,
                    fillColor: color,
                    opacity: 1,
                    fillPattern: fillP,
                    fillOpacity: newOptions.sectionOpacity,
                    weight: 2,
                    dashArray: null,
                    type: 'polygon'
                }

                let layer = L.polygon(swapCoords(sect.geom.coordinates), baseStyle)

                layer.baseStyle = baseStyle
                layer.data = {nr: sect.nr, localID: sect.localID, ref: prop.ref}

                layer.on('mouseover', () => {
                    if (leaflet.current?.state?.mode !== 'view') return;

                    const prevHovered = hoveredSectionLayerRef.current;
                    if (prevHovered && prevHovered !== layer && prevHovered !== selectedSectionLayerRef.current) {
                        applySectionBaseStyle(prevHovered);
                    }

                    if (selectedSectionLayerRef.current !== layer) {
                        applySectionHoverStyle(layer, newOptions.sectionOpacity);
                        hoveredSectionLayerRef.current = layer;
                    }
                })

                layer.on('mouseout', () => {
                    if (selectedSectionLayerRef.current === layer) return;

                    if (hoveredSectionLayerRef.current === layer) {
                        hoveredSectionLayerRef.current = null;
                    }
                    applySectionBaseStyle(layer);
                    if (selectedSectionLayerRef.current) {
                        selectedSectionLayerRef.current.bringToFront();
                    }
                    borders.bringToFront();
                })

                layer.on('click', () => {
                    if(leaflet.current.state.mode == 'view') {
                        openSectionDetails(sect.nr)

                        doRefresh(true)
                        if (selectedSectionLayerRef.current) {
                            applySectionBaseStyle(selectedSectionLayerRef.current);
                        }

                        selectedSectionLayerRef.current = layer;
                        applySectionSelectedStyle(layer);
                        layer.bringToFront();
                    }
                })

                if(layer.data.nr == selectedLayerNr){
                    if (selectedSectionLayerRef.current) {
                        applySectionBaseStyle(selectedSectionLayerRef.current);
                    }
                    selectedSectionLayerRef.current = layer;

                    applySectionSelectedStyle(layer);
                    doRefresh(true)
                }
                

                if(showSectNr) {
                    const label = L.tooltip(L.latLng(sect.label_location), {
                        drawn: true,
                        content: `${sect.localID}`,
                        className: 'black bg-transparent border-0 shadow-none',
                        direction: "center",
                        permanent: true,
                        opacity: 1
                    })
                    
                    label.addTo(labels);
                }
                if(sect.is_fucked){
                    layer.addTo(warningSections)
                } else{
                    layer.addTo(sections)
                }
            })

            leaflet.current.setLayers(sections)
            leaflet.current.setLayers(warningSections)

            newOptions.showBorders && borders.addTo(map)
            newOptions.showMarkers && markers.addTo(map)
            newOptions.showSections && sections.addTo(map)

            newOptions.showWarningSections && warningSections.addTo(map)

            if(showSectNr) {
                labels.addTo(map)
            }
        })

        borders.bringToFront()
        if(activeProps.length > 0 && zoomToProps) {
            const fitOpts = { maxZoom: 18, padding: [24, 24] };
            const fitBoundsToProps = () => {
                map.invalidateSize({ animate: false });
                const fromSections = sections.getLayers().length > 0 ? sections.getBounds() : null;
                const fromWarnings = warningSections.getLayers().length > 0 ? warningSections.getBounds() : null;
                const fromPolygons =
                    fromSections && fromWarnings
                        ? fromSections.extend(fromWarnings)
                        : fromSections ?? fromWarnings;
                if (fromPolygons?.isValid()) {
                    map.fitBounds(fromPolygons, fitOpts);
                } else if (borders.getLayers().length > 0) {
                    map.fitBounds(borders.getBounds(), fitOpts);
                }
            };
            requestAnimationFrame(() => requestAnimationFrame(fitBoundsToProps));
        }
        if (selectedSectionLayerRef.current) {
            selectedSectionLayerRef.current.bringToFront();
        }

        const mapContainer = map.getContainer();
        if (mapMouseLeaveHandlerRef.current) {
            mapContainer.removeEventListener('mouseleave', mapMouseLeaveHandlerRef.current);
        }
        mapMouseLeaveHandlerRef.current = () => {
            const hovered = hoveredSectionLayerRef.current;
            if (hovered && hovered !== selectedSectionLayerRef.current) {
                applySectionBaseStyle(hovered);
            }
            hoveredSectionLayerRef.current = null;
        };
        mapContainer.addEventListener('mouseleave', mapMouseLeaveHandlerRef.current);

        mapOptionsMount.current = false
    }

    /**
     * When SectionDetails calls openSectionInMap (view.leafletMode: view | edit | delete), enable that map mode
     * and optionally highlight or “click” the polygon so it is pre-selected for edit/delete.
     * Polygons live inside L.FeatureGroup layers, so findDrawnSectionPolygonByNr is required.
     */
    useEffect(() => {
        if (view.comp !== 'karta' || view.section == null || view.leafletMode == null) {
            return;
        }
        if (loading) {
            return;
        }

        const targetSection = view.section;
        const mode = view.leafletMode;

        const clearLeafletMode = () => {
            //This function is needed to force leaflet to reset to the correct view when editing/deleting is stopped
            const v = useNavigationStore.getState().view;
            if (v == null || v.leafletMode == null) {
                return;
            }
            setView({ ...v, leafletMode: undefined });
        };

        const finishOrFail = (foundLayer) => {
            if (foundLayer) {
                if (mode === 'edit' || mode === 'delete') {
                    try {
                        const b = foundLayer.getBounds();
                        if (b?.isValid()) {
                            const mapForBounds = foundLayer._map || leaflet.current?.state?.map;
                            mapForBounds?.fitBounds(b, { maxZoom: 20, duration: 0.5 });
                        }
                    } catch (e) {
                        /* empty */
                    }
                    setTimeout(() => {
                        flushSync(() => {
                            leaflet.current.setMode(mode);
                        });
                        setTimeout(() => {
                            foundLayer.fire('click');
                            doRefresh(true);
                            clearLeafletMode();
                        }, 50);
                    }, 0);
                    return;
                }
            }
            clearLeafletMode();
        };

        const findAndOpen = () => {
            const map = leaflet.current?.state?.map;
            if (mode === 'view') {
                setMapData(false, {}, copturePointLayers, targetSection);
                clearLeafletMode();
                return;
            }
            const targetLayer = findDrawnSectionPolygonByNr(map, targetSection);
            if (targetLayer) {
                finishOrFail(targetLayer);
                return;
            }
        };

        findAndOpen()
    }, [view.comp, view.section, view.leafletMode, loading, copturePointLayers, setView, doRefresh]);

    const handleErrorRequest = (resp) => {
        if(resp.status == 500) {
            setLoading(false)
            setErrorModal(true)
            throw new Error('Oops something went wrong');
        } else {
            return resp.json()
        }
    }

    // Killed feature: LM-orto is on its way out. This tells the backend to email
    // error@virkesborsen.se (with the user id) so we can see who still activates an
    // ortho layer. Fire-and-forget - delete with the rest of the LM-orto surface.
    const pingLmOrtoActivation = (propID, layer) => {
        fetch("/wms-proxy-lm-orto/activation-ping/", {
            method: "POST",
            credentials: 'same-origin',
            body: JSON.stringify({propID: propID, layer: layer}),
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': Cookies.get('csrftoken')
            },
        }).catch(() => {})
    }

    const updateMarker = () => {
        DemoBlockFunctionWrapper(() => {
            setLoading(true)

            fetch("/map-api/user-update-marker/", {
                method: "POST",
                body: JSON.stringify({
                    propID: markerModal.ref,
                    propType: markerModal.type,
                    markerId: markerModal.id,
                    markerName: markerModal.name,
                    markerComment: markerModal.comment,
                    markerIcon: markerModal.selected
                }),
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': Cookies.get('csrftoken')
                },
            }).then(resp => handleErrorRequest(resp)).then(data => {
                const propToUpdate = properties.find(p => p.ref === data.ref);
                if (propToUpdate) {
                    setOneProp({
                        ...propToUpdate,
                        data: data.new_data
                    });
                }
                setMarkerModal({show: false})
                setLoading(false)
                setMapData(false)
            });
        }, 
        DEMO_PROMPTS.UPDATE_MARKER)
    }

    const shapeCreateSubmit = (e, propID, propType) => {
        setLoading(true)
        if (e.layerType == 'marker') {
            DemoBlockFunctionWrapper(() => {
                fetch("/map-api/user-create-marker/", {
                method: "POST",
                body: JSON.stringify({
                    propID: propID,
                    propType: propType,
                    geom: e.layer.toGeoJSON(),
                    name: markerModal.name ? markerModal.name : '',
                    comment: markerModal?.comment ? markerModal.comment : '',
                    icon: markerModal.selected
                }),
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': Cookies.get('csrftoken')
                },
            }).then(resp => handleErrorRequest(resp)).then((data) => {
                const propToUpdate = properties.find(p => p.ref === propID);
                if (propToUpdate) {
                    setOneProp({
                        ...propToUpdate,
                        data: data
                    });
                }
                setMarkerModal({show: false})
                setMapData(false)
                setLoading(false)
                leaflet.current.stopDraw()
                doRefresh(true)
            });
            }, DEMO_PROMPTS.CREATE_MARKER, () => {setMarkerModal({show: false});setMapData(false);setLoading(false);leaflet.current.stopDraw();doRefresh(true)})
        } else {
            DemoBlockFunctionWrapper(() => {
            
            fetch("/map-api/user-create-sections/", {
                method: "POST",
                body: JSON.stringify({ propID: propID, propType: propType, geom: e.layer.toGeoJSON() }),
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': Cookies.get('csrftoken')
                },
            }).then(resp => handleErrorRequest(resp)).then((data) => {
                if("to_big" in data) {
                    setLoading(false)
                    setErrorModal({message: "Du har ritat en för stor avdelning"})
                    leaflet.current.state.map.removeLayer(e.layer)
                } else {
                    const propToUpdate = properties.find(p => p.ref === propID);
                    if (propToUpdate) {
                        setOneProp({ ...propToUpdate, data: data });
                    }
                    setMapData(false)
                    setLoading(false)
                    leaflet.current.stopDraw()
                    doRefresh(true)
                }
            });
            }, DEMO_PROMPTS.CREATE_SECTION, () => {setTimeout(() => {setLoading(false); setMapData(false); doRefresh(true)},50)})
        }
    }

    const handleShapeEdit = () => {
        DemoBlockFunctionWrapper(() => {
            setLoading(true)
            let geoms = {features: [], type: 'FeatureCollection'};

            leaflet.current.state.editingLayers.eachLayer(l => {
                let layer = l.toGeoJSON()
                layer.properties = l.data
                geoms.features.push(layer)
            })

            fetch("/map-api/user-edit-sections/", {
                method: "POST",
                body: JSON.stringify({ geoms: geoms }),
                headers: {
                    'Content-Type': 'application/json',
                    'X-CSRFToken': Cookies.get('csrftoken')
                },
            }).then(resp => handleErrorRequest(resp)).then((data) => {
                const updatedProps = properties.map(p => {
                    const update = data.find(i => i.ref === p.ref);
                    return update ? { ...p, data: update.new_data } : p;
                });
                setProps(updatedProps);
                setLoading(false)
            });
        }, DEMO_PROMPTS.SAVE_CHANGES)
    }

    const deleteMarker = async (id, ref, type) => {
        return fetch("/map-api/user-delete-marker/", {
            method: "POST",
            body: JSON.stringify({
                propID: ref,
                propType: type,
                markerId: id,
            }),
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': Cookies.get('csrftoken')
            },
        }).then(resp => handleErrorRequest(resp)).then((data) => {
            const propToUpdate = properties.find(p => p.ref === data.ref);
            if (propToUpdate) {
                setOneProp({ ...propToUpdate, data: data.new_data });
            }
        });
    }

    const handleShapeDelete = async () => {
        DemoBlockFunctionWrapper(() => {
            setLoading(true)

            let sections = []
            let markers = []

            leaflet.current.state.deletedLayers.eachLayer(l => {
                if(l.options.type == 'polygon') {
                    sections.push(l.data.nr)
                } else if(l.options.type == 'marker') {
                    markers.push({id: l.data.djangoID, ref: l.data.ref, type: l.data.type})
                }
            })

            let requests = []

            if(sections.length > 0) {
                requests.push(fetch("/map-api/user-delete-sections/", {
                    method: "POST",
                    body: JSON.stringify({ localIDs: sections }),
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRFToken': Cookies.get('csrftoken')
                    },
                }).then(resp => handleErrorRequest(resp)).then(data => {
                    const updatedProps = properties.map(p => {
                        const update = data.find(i => i.ref === p.ref);
                        return update ? { ...p, data: update.new_data } : p;
                    });
                    setProps(updatedProps);
                }))
            }
            
            if(markers.length > 0) {
                markers.map(async m => {
                    requests.push(deleteMarker(m.id, m.ref, m.type))
                })
            }

            Promise.all(requests).then(() => {
                setMapData(false)
                setLoading(false)
            })
        }, DEMO_PROMPTS.SAVE_CHANGES)
    }
    

    const finishDraw = (e) => {      
        if (activeProps.length == 1) {
            shapeCreateSubmit(e, activeProps[0].ref, activeProps[0].type)
        } else if (activeProps.length > 1) {
            let foundProp = null

            activeProps.map(prop => {
                if (prop.border && isPolygonWithinPolygon(e.layer.toGeoJSON(), prop.border)) {
                    foundProp = prop
                    shapeCreateSubmit(e, foundProp.ref, foundProp.type)
                }
            })
            if (!foundProp) {
                setSelectPropModal({props: activeProps, e: e})
            }
        } else {
            setErrorModal(true)
        }
    }

    const isPolygonWithinPolygon = (inner, outer) => {
        var isContained = true;

        outer.map(polygon => {
            if(inner.geometry.type == "Point") {
                if (!pointInPolygon(inner.geometry.coordinates, polygon[0])) {
                    isContained = false;
                }
            } else {
                inner.geometry.coordinates.map(shape => {
                    shape.map(point => {
                        if (!pointInPolygon(point, polygon[0])) {
                            isContained = false;
                        }
                    })
                })
            }
        })

        return isContained
    }

    

    const editButton = (text, icon, action, className='', id=null) => {
        const lockedFeature = !propDfr && !freemiumFeatures.includes(text);

        if (lockedFeature) {
            return (
                <div
                    id={id}
                    role='button'
                    onClick={() => openPlusBlockerDialog('dfr')}
                    className={`flex flex-col relative items-center justify-center rounded bg-white hover-bg-primary-core ${className}`}
                    style={{width: '65px', height: '65px'}}
                >
                    <img
                        alt={text}
                        src={`${Window.static_path}img/${icon}`}
                        className='mb-1'
                        height="20px"
                        width="20px"
                        style={{ whiteSpace: 'nowrap', opacity: 0.4 }}
                    />
                    <img
                            src={`${Window.static_path}img/icons/sparkle.svg`}
                            width="16"
                            height="16"
                            alt="Plus ikon"
                            className="absolute"
                            style={{top: "4px", right: "4px"}}
                        />
                    <div
                        className="flex flex-row items-center justify-center gap-1"
                        style={{ whiteSpace: 'nowrap', opacity: 0.4 }}
                    >
                        <span style={{fontSize: '12px'}}>{text}</span>
                    </div>
                </div>
            )
        }

        return <div id={id} role='button' onClick={action} className={`flex flex-col items-center justify-center rounded bg-white hover-bg-primary-core ${className}`} style={{width: '65px', height: '65px'}}>
            <img alt={text} src={`${Window.static_path}img/${icon}`} className='mb-1' height="20px" width="20px" />
            {text}
        </div>
    }

    const checkMapLayerActive = (name) => {
        let found = false
        
        leaflet.current.state?.mapLayers.eachLayer(l => {
            if(l.name == name) {
                found = true
            }
        })

        return found
    }

    
    const layerButton = (text, img, action, active, externalClassNames = "") => {
        const lockedFeature = !propDfr && !freemiumFeatures.includes(text);
        

        if (lockedFeature) {
            return (
                <div
                    role="button"
                    onClick={() => openPlusBlockerDialog('dfr')}
                    className={`relative flex flex-col items-center mb-1 mr-2 ${externalClassNames}`}
                    style={{ maxWidth: "60px" }}
                >
                    
                    <div className="relative" style={{ width: '50px', height: '50px' }}>
                        <div
                            className="absolute top-0 start-0 w-full h-full"
                            style={{
                                background: 'rgba(200,200,200,0.45)',
                                borderRadius: '6px',
                                zIndex: 1,
                                width: '50px',
                                height: '50px',
                            }}
                        />
                        <div
                            className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 flex items-center justify-center"
                            style={{
                                zIndex: 2,
                                pointerEvents: "none",
                                width: '50px',
                                height: '50px',
                            }}
                        >
                            <img
                                src={`${Window.static_path}img/icons/sparkle.svg`}
                                width="24"
                                height="24"
                                alt="Plus ikon"
                                style={{ filter: "drop-shadow(0 0 4px #fff)" }}
                            />
                        </div>
                        <img
                            alt={text}
                            src={`${Window.static_path}img/${img}`}
                            className={`rounded max-w-full h-auto ${active ? "border border-4 border-tertiary-main" : ""}`}
                            height="50"
                            width="50"
                            style={{
                                display: "block",
                                width: "50px",
                                height: "50px",
                                filter: "brightness(0.9)",
                                position: "relative",
                                zIndex: 0,
                            }}
                        />
                    </div>
                    {/* Text below stays clear */}
                    <span className="break-words text-center block" style={{ fontSize: "8px" }}>
                        {text}
                    </span>
                </div>
            );
        }
           

        return <div role='button' onClick={action} className={`flex flex-col items-center mb-1 mr-2 flex flex-col items-center mb-1 mr-2 ${externalClassNames}`} style={{maxWidth: '60px'}}>
            <img alt={text} src={`${Window.static_path}img/${img}`} className={`rounded ${active ? 'border border-4 border-tertiary-main' : ''}`} height='50px' width='50px' />
            <span className='break-words text-center' style={{fontSize: '8px'}}>{text}</span>
        </div>
    }

    const returnCoptureIcon = (style) => {
        return style == 'raster' ? 'drone.svg' : style == 'point' ? 'map-pin-area.svg' : 'stack-simple.svg'
    }

    const copturePointFeatureToIconName = (feature) => {
        const sym = feature?.properties?.SYMBOL
        if (typeof sym !== 'string') return 'Location'
        const content = sym.split(':')
        const typeTag = content[1]
        if (typeTag == 'Insektskada') {
            return 'LeafSimple'
        }
        if (typeTag == 'Vindfällen') {
            return 'WindSimple'
        }
        if (typeTag == 'Djur') {
            const animal = feature.properties.Djur
            if (animal == 'Vildsvin') {
                return 'PiggyBankSimple'
            }
            if (animal == 'R�djur') {
                return 'TreeEvergreenSimple'
            }
            if (animal == 'Älg' || animal == '�lg') {
                return 'TreeEvergreenFilled'
            }
            return 'PineTreeSimple'
        }
        return 'Location'
    }

    const getCoptureToggleRowIconName = (itemObj, style) => {
        if (style == 'raster') {
            return 'DroneSimple'
        }
        if (style == 'point') {
            const features = itemObj?.obj?.data?.features
            if (features?.length) {
                const pointFeature =
                    features.find((f) => f.geometry?.type === 'Point') || features[0]
                return copturePointFeatureToIconName(pointFeature)
            }
            return 'MapPinSimple'
        }
        return 'DotsNineSimple'
    }

    const coptureLayersActive = coptureLayerList(properties.filter(p => p.active))
    const coptureToggleRow = coptureLayersActive.map((item) => {
        if (item.obj.style == 'raster') {
            setCoptureLayer(item.obj)
            const active = leaflet.current?.state ? checkMapLayerActive(item.obj.layer) : false
            return { itemObj: item, active, style: item.obj.style }
        }
        if (item.obj.style == 'point') {
            const map = leaflet.current?.state?.map
            const pointLayer = copturePointLayers.find(l => l.obj.layer == item.obj.layer)
            const active = pointLayer?.featureGroup ? map.hasLayer(pointLayer.featureGroup) : false
            return { itemObj: pointLayer, active, style: item.obj.style }
        }
        return { itemObj: item, active: false, style: item.obj.style }
    })

    const mobileCoptureAnyActive = coptureToggleRow.some(({ active }) => active)

    const handleMobileCoptureToolboxClick = () => {
        if (coptureToggleRow.length > 1) {
            setMobileCoptureModalOpen(true)
        } else if (coptureToggleRow.length === 1) {
            toggleCoptureLayer(coptureToggleRow[0].itemObj)
        }
    }
    return <div className='relative overflow-hidden h-full'>
        <>
          <LockedInfoBanner className="absolute top-6 left-0 right-0 z-[500] max-w-[80%] lg:max-w-[90%] xl:max-w-[60%]" />
        </>
        <VBLoadingScreen zIndex={1035} show_loading={loading} className='absolute' />

        <VBLeaflet ref={leaflet} id='map_karta' baseMap='Mapbox Satelite' finishDraw={(e) => finishDraw(e)} />

        {coptureLayersActive.length > 0 ? 
            <CoptureMapContainer className='absolute z-[1010] hidden lg:block'>
                <div id='coptureContainer' className='flex flex-col bg-neutral-cloud shadow rounded text-[.6rem] leading-[.8rem] p-2'>
                    <div className='font-bold mb-1'>Drönanalys</div>
                    
                    <div className='flex'>
                        {coptureToggleRow.map(({ itemObj, active, style }, index) => {
                            const marginEnd = coptureToggleRow.length == index+1 ? '' : 'mr-1'
                            return <div key={`copture-desktop-${index}`} role='button' onClick={() => toggleCoptureLayer(itemObj)} className={`flex flex-col items-center justify-center rounded bg-white ${marginEnd} ${active ? 'bg-supp-forest-green' : 'hover-bg-supp-forest-green-300'}`} style={{width: '40px', height: '40px'}}>
                                <img
                                    className={active ? 'white-filter' : ''}
                                    src={`${Window.static_path}img/icons/${returnCoptureIcon(style)}`}
                                    height="30px"
                                    width="30px"
                                    alt=""
                                />
                            </div>
                        })}
                    </div>
                </div>
            </CoptureMapContainer> 
            : newCoptureLayer && <CoptureMapContainer className='absolute z-[1010]'>
                <div className='flex flex-col bg-white black shadow rounded p-6'>
                    <div className='flex justify-between'>
                        <p className='font-bold mb-2'>Klar för visning!</p>
                        <img role='button' onClick={() => setNewCoptureLayer(false)} src={`${Window.static_path}img/icons/cross.svg`} width="20px" height="20px" />
                    </div>

                    <p className='text-[.8rem] leading-[.9rem] mb-2'>Du har ett nytt drönarlager att se.</p>

                    <div>
                        <Button onClick={() => {
                            const updatedProps = properties.map(p => ({
                                ...p,
                                checked: p.ref === newCoptureLayer.ref,
                                active: p.ref === newCoptureLayer.ref
                            }));
                            setProps(updatedProps);
                            setVisaValda(true)
                            setNewCoptureLayer(false)
                        }}>Visa lager</Button>
                    </div>
                </div>
            </CoptureMapContainer>}
            
        <ToolboxContainer className='absolute z-[1010]'>
            <div className='flex flex-col bg-neutral-cloud shadow rounded p-1'>
                <div
                    role='button'
                    onClick={() => onToggleMapImmersive?.()}
                    className={`flex justify-center items-center rounded-sm ${activeProps.length > 0 ? 'mb-1' : ''} ${
                        mapImmersive
                            ? 'bg-supp-forest-green-300 hover-bg-supp-forest-green-300'
                            : 'bg-white hover-bg-supp-forest-green-300'
                    }`}
                    style={{height: '30px'}}
                    title={mapImmersive ? 'Visa meny' : 'Större karta'}
                    aria-label={mapImmersive ? 'Visa meny' : 'Större karta'}
                >
                    {mapImmersive ? (
                         <Icon 
                         name="ArrowsIn" 
                         className="text-charcoal-500"
                         size="large"/>
                    ) : (
                       <Icon 
                        name="ArrowsOut" 
                        className="text-charcoal-500"
                        size="large"/>
                    )}
                </div>
                <div role='button' onClick={() => leaflet.current.zoomIn()} className='flex justify-center items-center bg-white hover-bg-supp-forest-green-300 rounded-sm mb-1' style={{height: '30px'}}>
                    <img src={`${Window.static_path}img/icons/plus.svg`} height='20px' width='20px' />
                </div>
                <div role='button' onClick={() => leaflet.current.zoomOut()} className={`flex justify-center items-center bg-white hover-bg-supp-forest-green-300 rounded-sm ${
                    activeProps.length > 0
                        ? (coptureToggleRow.length > 0 ? 'mb-1 lg:mb-3' : 'mb-3')
                        : 'mb-1'
                }`} style={{height: '30px'}}>
                    <img src={`${Window.static_path}img/icons/minus.svg`} height='30px' width='30px' />
                </div>

                {activeProps.length > 0 && coptureToggleRow.length > 0 && (
                    <div className='lg:hidden mb-1'>
                        <div
                            role='button'
                            onClick={handleMobileCoptureToolboxClick}
                            className={`flex justify-center items-center rounded-sm ${mobileCoptureAnyActive ? 'bg-supp-forest-green' : 'bg-white hover-bg-supp-forest-green-300'}`}
                            style={{ height: '30px' }}
                        >
                            <Icon
                                name="DroneSimple"
                                size="large"
                                className={mobileCoptureAnyActive ? 'white-filter' : ''}
                            />
                        </div>
                    </div>
                )}

               

                {activeProps.length > 0 && <>
                    <div role='button' onClick={() => {setMapTools(mapTools == 'layers' ? false : 'layers'); leaflet.current.setMode('view'); doRefresh(true)}} className={`flex justify-center items-center ${mapTools == 'layers' ? 'bg-supp-forest-green' : 'bg-white hover-bg-supp-forest-green-300'} rounded-sm mb-1`} style={{height: '30px'}}>
                        <img id='leafletLayerButton' src={`${Window.static_path}img/icons/map-layers.svg`} className={mapTools == 'layers' ? 'white-filter' : ''} height='20px' width='20px' />
                    </div>

                    <div role='button' onClick={() => {setMapTools(mapTools == 'tools' ? false : 'tools'); leaflet.current.setMode('view'); leaflet.current?.state.control && leaflet.current.stopDraw(); doRefresh(true)}} className={`flex justify-center items-center ${mapTools == 'tools' ? 'bg-supp-forest-green' : 'bg-white hover-bg-supp-forest-green-300'} rounded-sm mb-1`} style={{height: '30px'}}>
                        <img id='toolkitButton' src={`${Window.static_path}img/icons/map-edit.svg`} className={mapTools == 'tools' ? 'white-filter' : ''} height='20px' width='20px' />
                    </div>

                    <div role='button' onClick={() => setShowLegend(!showLegend)} className={`flex justify-center items-center ${showLegend ? 'bg-supp-forest-green' : 'bg-white hover-bg-supp-forest-green-300'} rounded-sm mb-1`} style={{height: '30px'}}>
                        <img src={`${Window.static_path}img/icons/info_filled.svg`} className={showLegend ? 'white-filter' : ''} height='20px' width='20px' />
                    </div>

                </>}
            </div>
        </ToolboxContainer>

        <TailwindWrapper>
            <Drawer
                isOpen={mobileCoptureModalOpen && coptureToggleRow.length > 1}
                onClose={() => setMobileCoptureModalOpen(false)}
            >
                <Drawer.Header className="items-center">
                    <Text variant="text-lg/leading-none/medium">Drönanalys</Text>
                    <Button
                        variant="tertiary"
                        size="icon"
                        onClick={() => setMobileCoptureModalOpen(false)}
                    >
                        <Icon  
                            name="CrossSimple"
                            className="color-charcoal-500"
                            size="large"/>
                    </Button>
                </Drawer.Header>
                <Drawer.Content className="flex flex-col gap-2">
                        {coptureToggleRow.map(({ itemObj, active, style }, index) => (
                            <div
                                key={`copture-mobile-modal-${index}`}
                                className="flex flex-row items-center gap-3 rounded-lg bg-[#F9F9F9] p-3"
                            >
                                <div className="flex min-w-0 flex-1 flex-row items-center gap-3">
                                    <Icon
                                        name={getCoptureToggleRowIconName(itemObj, style)}
                                        size="large"
                                        className="p-2 rounded-lg bg-white"
                                    />
                                    <div className="flex flex-col gap-2">
                                        <Text variant="text-sm/leading-normal/medium" className="capitalize">
                                            {itemObj?.designation?.toLowerCase() ?? `Lager ${index + 1}`}
                                        </Text>
                                        <Text variant="text-xs/leading-normal/medium" className="text-[#737373]">
                                            {itemObj?.obj?.layer_visual_name}
                                        </Text>
                                    </div>
                                </div>
                                <Switch
                                    checked={active}
                                    onChange={(nextChecked) => {
                                        if (nextChecked !== active) {
                                            toggleCoptureLayer(itemObj);
                                        }
                                    }}
                                />
                            </div>
                        ))}
                </Drawer.Content>
                <Drawer.Footer>
                    <Button variant="primary" className="w-full" onClick={() => setMobileCoptureModalOpen(false)}>
                        Stäng
                    </Button>
                </Drawer.Footer>
            </Drawer>
        </TailwindWrapper>
        
        {/* Change map UI visuals */}
        <EditUIContainer className={`${mapTools == 'tools' ? 'flex' : 'hidden'} flex-col bg-neutral-cloud shadow rounded z-[1040] absolute p-1`}>
            <div className='bg-white caption p-2'>
                <p className='font-bold mb-0'>Redigera kartutseendet <img src={`${Window.static_path}img/icons/cross.svg`} className='float-right' role='button' width='20px' height='20px' onClick={() => setMapTools(false)} /></p>

                {!refresh ? <div className="flex flex-col gap-2 my-3">
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={mapOptions.showBorders}
                            onChange={(checked) => {handleUserSubscription(() => { setMapOptions((prev) => ({ ...prev, showBorders: checked }))}, () => openPlusBlockerDialog('dfr'), true)}}
                        />
                        <Text variant="text-sm/leading-normal/medium">Gränser</Text>
                    </div>
                    
                    <div className="flex items-center gap-2">
                        <Switch
                            checked={mapOptions.showMarkers}
                            onChange={(checked) => {handleUserSubscription(() => { setMapOptions((prev) => ({ ...prev, showMarkers: checked }))}, () => openPlusBlockerDialog('dfr'), true)}}
                        />
                        <Text variant="text-sm/leading-normal/medium">Markörer</Text>
                    </div>

                    <div className="flex items-center gap-2">
                        <Switch
                            checked={mapOptions.showSections}
                            onChange={(checked) => {handleUserSubscription(() => { setMapOptions((prev) => ({ ...prev, showSections: checked }))}, () => openPlusBlockerDialog('dfr'), true)}}
                        />
                        <Text variant="text-sm/leading-normal/medium">Avdelningar</Text>
                    </div>

                    <div className="flex items-center gap-2">
                        <Switch
                            checked={mapOptions.showWarningSections}
                            onChange={(checked) => {handleUserSubscription(() => { setMapOptions((prev) => ({ ...prev, showWarningSections: checked }))}, () => openPlusBlockerDialog('dfr'), true)}}
                        />
                        <Text variant="text-sm/leading-normal/medium">Avdelningar med varning</Text>
                    </div>
                        
                </div> : <div style={{height: '87px'}}></div>}
                
                <p className='mb-0 mt-2'>Ändra genomskinlighet</p>
                <SliderBar
                    className="w-full my-2"
                    value={[mapOptions.sectionOpacity * 10]}
                    min={0}
                    max={10}
                    step={1}
                    onValueChange={(value) => {
                        handleUserSubscription(
                            () => setMapOptions((prev) => ({ ...prev, sectionOpacity: Number(value[0]) / 10 })),
                            () => openPlusBlockerDialog('dfr'),
                            true
                        )
                    }}
                />
                
                <div className='flex justify-between items-center'>
                    <button type='button' className='transition-all duration-300 ease-out text-[.8em] text-center rounded-[30px] border text-white bg-[#023436] border-[#023436] disabled:text-[#9d9fa1] disabled:bg-[#f5f5f5] disabled:border-[#f5f5f5] enabled:hover:bg-[#144143] enabled:hover:border-[#144143] enabled:active:bg-[#012526] enabled:active:border-[#012526] rounded !py-1 !px-2' onClick={() => setMapTools(false)}>Stäng</button>
                    <button type='button' className='transition-all duration-300 ease-out text-[.8em] text-center rounded-[30px] border text-white bg-[#023436] border-[#023436] disabled:text-[#9d9fa1] disabled:bg-[#f5f5f5] disabled:border-[#f5f5f5] enabled:hover:bg-[#144143] enabled:hover:border-[#144143] enabled:active:bg-[#012526] enabled:active:border-[#012526] rounded !py-1 !px-2' onClick={() => {setMapOptions({showBorders: true, showSections: true, showWarningSections:true, showMarkers: true, sectionOpacity: coptureLayerList(properties.filter(p => p.active == true)).length > 0 ? .3 : .7 }); doRefresh(true)}}>Återställ</button>
                </div>
            </div>
        </EditUIContainer>
        
        {/* Map legend information */}
        <div className={`${showLegend ? 'flex' : 'hidden'} flex-col bg-neutral-cloud shadow rounded z-[1010] absolute p-1 mt-6`} style={{width: '250px', right: '10px', bottom: '30px'}}>
            <div className='bg-white text-[.6rem] leading-[.8rem] p-2'>
                <div className='flex justify-between items-center mb-1'>
                    <b>Huggningsklasser</b>
                    <img
                        role='button'
                        src={`${Window.static_path}img/icons/cross.svg`}
                        width='20px'
                        height='20px'
                        onClick={() => setShowLegend(false)}
                        aria-label='Stäng legend'
                    />
                </div>
                {legend.sort((a, b) => fellingClassesOrder.indexOf(a) == -1 ? 1 : fellingClassesOrder.indexOf(b) == -1 ? -1 : fellingClassesOrder.indexOf(a) - fellingClassesOrder.indexOf(b)).map(i => {
                    return <div className='flex mb-1'>
                        <div className='mr-1' style={{backgroundColor: FellingClassesLegend[i] ? FellingClassesLegend[i].color : '#bb0000', height: '16px', minWidth: '16px'}}></div><b>{i}</b>: {FellingClassesLegend[i] ? FellingClassesLegend[i].text : 'Okänd'}
                    </div>
                })}
            </div>
        </div>

        <MapTools className={`z-[1010] ${mapTools || leaflet.current?.state?.mode != 'view' || (!markerModal.show && markerModal.selected) || leaflet.current?.state.control ? 'show' : ''} ${mapTools == 'layers' ? 'layers-open absolute' : mapImmersive ? 'absolute' : 'fixed'} ${mapImmersive ? 'map-tools-immersive' : ''}`}>
            <div className={`flex flex-col bg-neutral-cloud shadow rounded-top text-[.6rem] leading-[.8rem] px-2 pt-2 ${mapTools == 'layers' ? 'h-full overflow-hidden' : ''}`} style={mapTools == 'layers' ? { minHeight: 0 } : undefined}>
                {leaflet.current?.state.control && <>
                    <div className='text-center font-bold mb-1'>Du ritar nu</div>
                    
                    <div className='flex pb-2'>
                        {editButton('Spara', 'icons/save.svg', () => {leaflet.current.completeDraw(); doRefresh(true)}, 'mr-2')}

                        {editButton('Avbryt', 'icons/ui-close.svg', () => {leaflet.current.stopDraw(); doRefresh(true)})}
                    </div>
                </>}

                {(leaflet.current?.state?.mode == 'edit' || leaflet.current?.state?.mode == 'delete') && <>
                    <div className='text-center font-bold mb-1'>{leaflet.current?.state?.mode == 'edit' ? 'Du redigerar nu' : 'Du tar nu bort'}</div>
                    
                    <div className='flex pb-2'>
                        {editButton('Spara', 'icons/save.svg', () => {leaflet.current.state.mode == 'edit' ? handleShapeEdit() : handleShapeDelete(); leaflet.current.save(); doRefresh(true)}, 'mr-2')}

                        {editButton('Avbryt', 'icons/ui-close.svg', () => {leaflet.current.revert(); setMapData(false); doRefresh(true)})}
                    </div>
                </>}

                {!markerModal.show && markerModal.selected && !leaflet.current?.state.control && <>
                    <div className='text-center font-bold mb-1'>Placera markör</div>
                    
                    <div className='flex justify-center pb-2'>
                        {editButton('Avbryt', 'icons/ui-close.svg', () => {leaflet.current.stopDraw(); setMarkerModal({show: false})})}
                    </div>
                </>}

                {mapTools == 'tools' && <>
                    <div className='text-center font-bold mb-1'>Verktyg:</div>

                    <div className='flex pb-2'>
                        {activeCanEdit ? (
                            <>
                                {editButton('Rita', 'icons/ui-edit.svg', () => {setMapTools(false); leaflet.current.startDraw('Polygon'); doRefresh(true)}, 'mr-2', 'drawButton')}

                                {editButton('Redigera', 'icons/pencil-square.svg', () => {setMapTools(false); leaflet.current.setMode('edit'); doRefresh(true)}, 'mr-2', 'editSectionButton')}

                                {editButton('Radera', 'icons/delete.svg', () => {setMapTools(false); leaflet.current.setMode('delete'); doRefresh(true)}, 'mr-2', 'deleteSectionButton')}

                                {editButton('Markör', 'icons/marker.svg', () => {setMapTools(false); setMarkerModal({show: true, selected: null})}, 'mr-2')}
                            </>
                        ) : (
                            <div className='text-center text-[#737373] px-2 pb-1'>
                                Du har visningsbehörighet för den här fastigheten.
                            </div>
                        )}

                        {editButton('Min plats', 'icons/my-location.svg', () => {setMapTools(false); setUserLocation(leaflet.current.state.map)}, 'mr-2', 'getLocation')}
                    </div>
                </>}

                {mapTools == 'layers' && <div className='bg-white p-2 flex-grow overflow-auto overflow-y-auto' style={{ minHeight: 0, WebkitOverflowScrolling: 'touch' }}>
                    <div className='mb-2 font-bold'>Välj bakgrundskarta: <img src={`${Window.static_path}img/icons/cross.svg`} className='float-right' role='button' width='20px' height='20px' onClick={() => setMapTools(false)} /></div>
                    <div className='flex flex-wrap'>
                        {layerButton('Satellit', 'illustration/Mapbox-satelite.png', () => {leaflet.current.setBaseMap('Mapbox Satelite'); doRefresh(true)}, leaflet.current.state.baseMap.name == 'Mapbox Satelite', 'mr-2')}

                        {layerButton('Mapbox Plain', 'illustration/Mapbox-plain.png', () => {leaflet.current.setBaseMap('Mapbox Plain'); doRefresh(true)}, leaflet.current.state.baseMap.name == 'Mapbox Plain', 'mr-2')}

                        {layerButton('Open Street Map', 'illustration/Open-Street-Map.webp', () => {leaflet.current.setBaseMap('Open Street Map'); doRefresh(true)}, leaflet.current.state.baseMap.name == 'Open Street Map')}
                    
                        {activeProps.map((itm) => {
                            return itm.data?.ortho_photos?.map((orto) => {
                                return layerButton(orto.name, 'illustration/Object-med-naturvarde.webp', () => {pingLmOrtoActivation(itm.ref, orto.layer); leaflet.current.setBaseMap(orto); doRefresh(true)}, leaflet.current.state.baseMap.name == orto.name)
                            })
                        })}
                    </div>

                    <div className='mb-2 font-bold'>Växla lager:</div>
                    <div className='flex flex-wrap'>

                        {layerButton('Avd. nr', 'illustration/Avdelnings-nr.webp', () => {setShowSectNr(!showSectNr)}, showSectNr)}

                        {layerButton('Avverkningsanmälan', 'illustration/Avverkningsanmalan.png', () => {leaflet.current.toggleMapLayer('Avverkningsanmälan'); doRefresh(true)}, checkMapLayerActive('Avverkningsanmälan'), 'mr-2')}

                        {layerButton('Utförd avverkning', 'illustration/Utford-avverkning.png', () => {leaflet.current.toggleMapLayer('Utförd avverkning'); doRefresh(true)}, checkMapLayerActive('Utförd avverkning'), 'mr-2')}

                        {layerButton('Nyckelbiotoper', 'illustration/Nyckelbiotoper.png', () => {leaflet.current.toggleMapLayer('Nyckelbiotoper'); doRefresh(true)}, checkMapLayerActive('Nyckelbiotoper'), 'mr-2')}

                        {layerButton('Fornlämningar', 'illustration/Fornlamningar.png', () => {leaflet.current.toggleMapLayer('Fornlämningar'); doRefresh(true)}, checkMapLayerActive('Fornlämningar'), 'mr-2')}

                        {layerButton('Vattenskyddsområden', 'illustration/Vattenskyddsomraden.png', () => {leaflet.current.toggleMapLayer('Vattenskyddsområden'); doRefresh(true)}, checkMapLayerActive('Vattenskyddsområden'), 'mr-2')}
                        
                        {layerButton('Stormen Johannes', 'illustration/stormen-johannes.png', () => {leaflet.current.toggleMapLayer('Stormen Johannes'); doRefresh(true); if(checkMapLayerActive('Stormen Johannes')){setMapOptions({...mapOptions, sectionOpacity:0}); setInfoModal({title: "Förändringsbild över stormen Johannes verkningar", message: <span>Områden som förmodligen har drabbats av stormen lyser upp som ljusgröna områden. <a target="_blank" style={{textDecoration: "underline"}} href="https://treebula-bucket.s3.eu-west-1.amazonaws.com/marketing/Information_%C3%B6versiktlig_indikation_stormskador.pdf">Läs mer här</a></span>, extra: <span><br/><br/>Källa: Skogsstyrelsen</span>})} else {setMapOptions({...mapOptions, sectionOpacity:0.7})}}, checkMapLayerActive('Stormen Johannes'), 'mr-2')}
                    </div>

                    <div className='flex flex-wrap'>

                        {layerButton('Object med naturvärde', 'illustration/Object-med-naturvarde.webp', () => {leaflet.current.toggleMapLayer('Object med naturvärde'); doRefresh(true)}, checkMapLayerActive('Object med naturvärde'))}
                        
                        {layerButton('Sumpskog', 'illustration/Sumpskog.webp', () => {leaflet.current.toggleMapLayer('Sumpskog'); doRefresh(true)}, checkMapLayerActive('Sumpskog'))}

                        {layerButton('Observationer invasiva arter', 'illustration/Observationer-invasiva-arter.webp', () => {leaflet.current.toggleMapLayer('Observationer invasiva arter'); doRefresh(true)}, checkMapLayerActive('Observationer invasiva arter'))}

                        {layerButton('Torvmark', 'illustration/torvmark.webp', () => {leaflet.current.toggleMapLayer('Torvmark'); doRefresh(true)}, checkMapLayerActive('Torvmark'))}

                        {layerButton('Observationer rödlistade arter', 'illustration/Observationer-rodlistade-arter.webp', () => {leaflet.current.toggleMapLayer('Observationer rödlistade arter'); doRefresh(true)}, checkMapLayerActive('Observationer rödlistade arter'))}

                        {layerButton('NVIS Kolbotten', 'illustration/nvis-kolbotten.webp', () => {leaflet.current.toggleMapLayer('NVIS Kolbotten'); doRefresh(true)}, checkMapLayerActive('NVIS Kolbotten'), 'me-2')}

                        {layerButton('NVIS Fångstgrop', 'illustration/nvis-fangstgrop.webp', () => {leaflet.current.toggleMapLayer('NVIS Fångstgrop'); doRefresh(true)}, checkMapLayerActive('NVIS Fångstgrop'), 'me-2')}

                    </div>
                </div>}
            </div>
        </MapTools>

        {/* INFO Modal */}
        {createPortal(
            <>
                <Dialog  isOpen={!!infoModal} onClose={() => setInfoModal(false)} size="small">
                    <Dialog.Header >
                        <Text variant="text-lg/leading-normal/medium">{infoModal.title}</Text>
                        <Button
                            variant="tertiary"
                            size="icon"
                            onClick={() => setInfoModal(false)}
                        >
                            <Icon
                            name="CrossSimple"
                            className="color-charcoal-500"
                            size="medium"
                            />
                        </Button>
                    </Dialog.Header>
                    <Dialog.Content >
                        <Text variant="text-sm/leading-normal/normal">{infoModal.message}</Text>
                        {infoModal.extra && <Text variant="text-xs/leading-normal/normal">{infoModal.extra}</Text>}
                    </Dialog.Content>
                    <Dialog.Footer>
                        <Button variant="primary" onClick={() => setInfoModal(false)}>Stäng</Button>
                    </Dialog.Footer>
                </Dialog>
            </>,
            document.body
        )}
      
        {/* MARKER PLACEMENT POPUP */}
        <TwModal isOpen={!!markerModal.show} className="bg-neutral-cloud p-2">
            <TwModal.Header>
                <div className="flex justify-between items-start">
                    <h2 className="neutral-charcoal mb-0 text-[49px] leading-[105%] tracking-[-1.47px] max-[768px]:text-[25px] max-[768px]:leading-[110%] max-[768px]:tracking-[-0.75px]">{markerModal.id ? 'Redigera' : 'Skapa'} Markör</h2>
                    <button type="button" className="ml-auto text-[20px] leading-[120%] tracking-[-0.4px] leading-none" onClick={() => setMarkerModal({show: false})} aria-label="Close">&times;</button>
                </div>
            </TwModal.Header>
            <TwModal.Content className="neutral-charcoal">
                <p>Markörer hjälper dig hålla kolla på viktiga punkter i din skog.</p>
                <p className="mb-1">Välj ikon:</p>

                {markerModal.error && <p className="text-danger">{markerModal.error}</p>}

                {mapMarkers.map(m => <div role='button' onClick={() => setMarkerModal({...markerModal, selected: m})} className={`m-2 inline-flex justify-center rounded ${markerModal.selected == m ? 'bg-neutral-cloud-700' : 'hover-bg-neutral-cloud-700'}`} style={{ width: "64px", height: "72px", transition: ".3s" }}>
                        <img src={m} className="p-1" width="48px" />
                    </div>
                )}

                <p className="mb-1 mt-2">Namn</p>
                <VBInput
                    initial={markerModal?.name}
                    onChange={(val) => markerModal.name = val} />

                <p className="mb-1 mt-2">Beskrivning</p>
                <VBTextarea
                    initial={markerModal?.comment}
                    onChange={(val) => markerModal.comment = val} 
                    resize="none" 
                    height="200px" />
            </TwModal.Content>
            <TwModal.Footer>
                <button className='transition-all duration-300 ease-out text-[.8em] text-center py-[11px] px-6 rounded-[30px] border text-white bg-[#023436] border-[#023436] disabled:text-[#9d9fa1] disabled:bg-[#f5f5f5] disabled:border-[#f5f5f5] enabled:hover:bg-[#144143] enabled:hover:border-[#144143] enabled:active:bg-[#012526] enabled:active:border-[#012526] rounded w-full my-2' onClick={() => {
                    if(!markerModal.selected) {
                        setMarkerModal({...markerModal, error: '*Vänligen välj en markör innan du fortsätter.'})
                    } else if(markerModal.id) {
                        updateMarker()
                    } else {
                        leaflet.current.startDraw('Marker', {
                            icon: L.icon({
                                iconUrl: markerModal.selected,
                                iconSize: [38, 95],
                                iconAnchor: [18, 74]
                            })
                        })
                        setMarkerModal({...markerModal, show: false})
                    }
                }}>
                    {markerModal.id ? 'Spara' : 'Välj plats på kartan'}
                </button>

                {markerModal.id && <button className='transition-all duration-300 ease-out text-[.8em] text-center py-[11px] px-6 rounded-[30px] border text-[#2a2e33] bg-transparent border-[#023436] disabled:text-[#9d9fa1] disabled:bg-[#f5f5f5] disabled:border-[#f5f5f5] enabled:hover:bg-[rgba(12,54,54,0.07)] enabled:active:bg-[rgba(3,51,53,0.26)] rounded w-full my-2' onClick={() => {setDeleteMarkerModal({id: markerModal.id, ref: markerModal.ref, type: markerModal.type}); setMarkerModal({show: false})}}>Radera</button>}
            </TwModal.Footer>
        </TwModal>
        
        {/* CONFIRM MARKER DELETE Modal */}
        <TwModal isOpen={!!deleteMarkerModal} className="bg-neutral-cloud p-2">
            <TwModal.Header>
                <div className="flex justify-between items-start">
                    <h3 className="neutral-charcoal mb-0 text-[49px] leading-[105%] tracking-[-1.47px] max-[768px]:text-[25px] max-[768px]:leading-[110%] max-[768px]:tracking-[-0.75px]">Är du säker på att du vill radera?</h3>
                    <button type="button" className="ml-auto text-[20px] leading-[120%] tracking-[-0.4px] leading-none" onClick={() => setDeleteMarkerModal(false)} aria-label="Close">&times;</button>
                </div>
            </TwModal.Header>
            <TwModal.Footer>
                <div className="flex justify-between">
                    <button type="button" className="transition-all duration-300 ease-out text-[.8em] text-center py-[11px] px-6 rounded-[30px] border text-white bg-[#023436] border-[#023436] disabled:text-[#9d9fa1] disabled:bg-[#f5f5f5] disabled:border-[#f5f5f5] enabled:hover:bg-[#144143] enabled:hover:border-[#144143] enabled:active:bg-[#012526] enabled:active:border-[#012526]" onClick={() => setDeleteMarkerModal(false)}>Nej</button>
                    <button type="button" className="transition-all duration-300 ease-out text-[.8em] text-center py-[11px] px-6 rounded-[30px] border text-white bg-[#023436] border-[#023436] disabled:text-[#9d9fa1] disabled:bg-[#f5f5f5] disabled:border-[#f5f5f5] enabled:hover:bg-[#144143] enabled:hover:border-[#144143] enabled:active:bg-[#012526] enabled:active:border-[#012526]" onClick={() => {
                        setLoading(true)
                        deleteMarker(deleteMarkerModal.id, deleteMarkerModal.ref, deleteMarkerModal.type).then(() => {
                            setDeleteMarkerModal(false)
                            setMapData(false)
                            setLoading(false)
                        })
                    }}>Radera</button>
                </div>
            </TwModal.Footer>
        </TwModal>
        
        {/* CONFIRM PROP Modal */}
        <TwModal isOpen={!!selectPropModal} className="bg-neutral-cloud p-2">
            <TwModal.Header>
                <div className="flex justify-between items-start">
                    <h2 className="neutral-charcoal mb-0 text-[49px] leading-[105%] tracking-[-1.47px] max-[768px]:text-[25px] max-[768px]:leading-[110%] max-[768px]:tracking-[-0.75px]">Vilken fastighet redigerar du?</h2>
                    <button type="button" className="ml-auto text-[20px] leading-[120%] tracking-[-0.4px] leading-none" onClick={() => {leaflet.current.state.map.removeLayer(selectPropModal.e.layer); setSelectPropModal(false)}} aria-label="Close">&times;</button>
                </div>
            </TwModal.Header>
            <TwModal.Content>
                <p className='neutral-charcoal'>Eftersom du har flera fastigheter synligt behöver vi veta vilken fastighet du redigerar</p>

                {selectPropModal && <VBDropdown
                    options={selectPropModal.props.map(p => {return {label: p.designation, value: p.ref, type: p.type}})}
                    onChange={(val) => setSelectPropModal({...selectPropModal, selected: val})}
                    error={selectPropModal?.error}
                    onFocus={() => setSelectPropModal({...selectPropModal, error: ''})} />}
            </TwModal.Content>
            <TwModal.Footer>
                <div className="flex justify-end">
                    <button type="button" className="transition-all duration-300 ease-out text-[1em] text-center py-[11px] px-8 rounded-[30px] border text-white bg-[#023436] border-[#023436] max-[992px]:py-[11px] max-[992px]:px-6 max-[992px]:text-[.8em] disabled:text-[#9d9fa1] disabled:bg-[#f5f5f5] disabled:border-[#f5f5f5] enabled:hover:bg-[#144143] enabled:hover:border-[#144143] enabled:active:bg-[#012526] enabled:active:border-[#012526]" onClick={() => {
                        if(!("selected" in selectPropModal)) {
                            setSelectPropModal({...selectPropModal, error: 'Du måste välja en fastighet för att fortsätta'})
                        } else {
                            let prop = selectPropModal.props.find(p => p.ref == selectPropModal.selected.value)
                            shapeCreateSubmit(selectPropModal.e, prop.ref, prop.type)
                            setSelectPropModal(false)
                        }
                    }}>Fortsätt</button>
                </div>
            </TwModal.Footer>
        </TwModal>

        {/* ERROR Modal */}
        <ErrorModal show={errorModal} title={errorModal?.title} message={errorModal?.message} onToggle={() => setErrorModal(false)} />

        <Joyride
            run={joyride}
            continuous={true}
            steps={infoSteps}
            stepIndex={helpSteps}
            scrollDuration={0}
            scrollOffset={100}
            showSkipButton={true}
            showProgress={true}
            hideCloseButton={true}
            locale={{
                back: 'Tillbaka',
                close: 'Stäng',
                last: 'Kom igång',
                next: 'Nästa',
                skip: 'Hoppa över'
            }}
            styles={{
                options: {
                    textColor: '#2a2e33',
                    primaryColor:'#023436',
                    zIndex: 1900
                },
            }}
            callback={async data => {
                if(data.status != 'running') {
                    setMapTools(false)
                    setShowJoyride(false)
                    setHelpSteps(0)
                }
                if(data?.lifecycle == 'complete') {
                    if(data.action == 'prev') {
                        data.step.target == '#drawButton' && setMapTools(false)
                        data.step.target == 'path.leaflet-interactive' && setMapTools('tools')

                        if(data.step.target == '#menuoverblick' || data.step.target == '#quickmenuoverblick') {
                            const layers = []
                            leaflet.current.state.map.eachLayer(l => l.options.drawn && l.data?.localID && layers.push(l))
                            await layers[0].fireEvent('click');
                            await new Promise(() => setTimeout(() => setHelpSteps(helpSteps - 1), 300))
                        } else {
                            setHelpSteps(helpSteps - 1)
                        }
                    }
                    if(data.action == 'next' || data.action == 'close') {
                        data.step.target == '#toolkitButton' && setMapTools('tools')
                        data.step.target == '#getLocation' && setMapTools(false)

                        if(data.step.target == 'path.leaflet-interactive') {
                            const layers = []
                            leaflet.current.state.map.eachLayer(l => l.options.drawn && l.data?.localID && layers.push(l))
                            await layers[0].fireEvent('click');
                            await new Promise(() => setTimeout(() => setHelpSteps(helpSteps + 1), 300))
                        } else {
                            setHelpSteps(helpSteps + 1)
                        }
                    }
                }
            }} />
    </div>
})

export default Karta;